import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingScreen from '../common/LoadingScreen';
import ProtectedRoute from './ProtectedRoute';
import PublicRoute from './PublicRoute';

// Lazy load pages for better performance
const EnterpriseLandingPage = React.lazy(() => import('../../pages/EnterpriseLandingPage'));
const EnterpriseLoginPage = React.lazy(() => import('../../pages/auth/EnterpriseLoginPage'));
const RegisterPage = React.lazy(() => import('../../pages/auth/RegisterPage'));
const ForgotPasswordPage = React.lazy(() => import('../../pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('../../pages/auth/ResetPasswordPage'));

const QuestionnairePage = React.lazy(() => import('../../pages/onboarding/QuestionnairePage'));
const OnboardingFlow = React.lazy(() => import('../../pages/onboarding/OnboardingFlow'));

const DashboardPage = React.lazy(() => import('../../pages/dashboard/DashboardPage'));
const ProfilePage = React.lazy(() => import('../../pages/profile/ProfilePage'));
const SettingsPage = React.lazy(() => import('../../pages/settings/SettingsPage'));

// Gamification
const GamificationHub = React.lazy(() => import('../../pages/gamification/GamificationHub'));
const QuizPage = React.lazy(() => import('../../pages/gamification/QuizPage'));
const LeaderboardPage = React.lazy(() => import('../../pages/gamification/LeaderboardPage'));
const AchievementsPage = React.lazy(() => import('../../pages/gamification/AchievementsPage'));
const ChallengesPage = React.lazy(() => import('../../pages/gamification/ChallengesPage'));
const PointsHistoryPage = React.lazy(() => import('../../pages/gamification/PointsHistoryPage'));
const AchievementDetailPage = React.lazy(() => import('../../pages/gamification/AchievementDetailPage'));
const GameDetailPage = React.lazy(() => import('../../pages/gamification/GameDetailPage'));
const ChallengeDetailPage = React.lazy(() => import('../../pages/gamification/ChallengeDetailPage'));
const LeaderboardDetailPage = React.lazy(() => import('../../pages/gamification/LeaderboardDetailPage'));

const GamesHub = React.lazy(() => import('../../pages/games/GamesHub'));
const MemoryGame = React.lazy(() => import('../../pages/games/MemoryGame'));
const WordPuzzleGame = React.lazy(() => import('../../pages/games/WordPuzzleGame'));
const TriviaGame = React.lazy(() => import('../../pages/games/TriviaGame'));
const ReactionGame = React.lazy(() => import('../../pages/games/ReactionGame'));
const SpeedMathGame = React.lazy(() => import('../../pages/games/SpeedMathGame'));
const PatternGame = React.lazy(() => import('../../pages/games/PatternGame'));
const NarrativeQuestEngine = React.lazy(() => import('../../pages/games/NarrativeQuestEngine'));
const ScavengerExpeditionEngine = React.lazy(() => import('../../pages/games/ScavengerExpeditionEngine'));
const WorldBuildingEngine = React.lazy(() => import('../../pages/games/WorldBuildingEngine'));
const RolePlayingEngine = React.lazy(() => import('../../pages/games/RolePlayingEngine'));
const MicroChallengeEngine = React.lazy(() => import('../../pages/games/MicroChallengeEngine'));
const FitnessCoachGame = React.lazy(() => import('../../pages/games/FitnessCoachGame'));
const WellnessQuizGame = React.lazy(() => import('../../pages/games/WellnessQuizGame'));
const MealPlannerGame = React.lazy(() => import('../../pages/games/MealPlannerGame'));

// Learning
const LearningHub = React.lazy(() => import('../../pages/learning/LearningHub'));
const CoursePage = React.lazy(() => import('../../pages/learning/CoursePage'));
const LessonPlayer = React.lazy(() => import('../../pages/learning/LessonPlayer'));
const CertificatePage = React.lazy(() => import('../../pages/learning/CertificatePage'));
const ArticlePage = React.lazy(() => import('../../pages/learning/ArticlePage'));
const VideoPage = React.lazy(() => import('../../pages/learning/VideoPage'));
const PodcastPage = React.lazy(() => import('../../pages/learning/PodcastPage'));
const MyLearningPage = React.lazy(() => import('../../pages/learning/MyLearningPage'));
const MicrolearningPage = React.lazy(() => import('../../pages/learning/MicrolearningPage'));
const CompanyPoliciesPage = React.lazy(() => import('../../pages/learning/CompanyPoliciesPage'));
const PolicyDetailPage = React.lazy(() => import('../../pages/policies/PolicyDetailPage'));
const CourseDetailPage = React.lazy(() => import('../../pages/learning/CourseDetailPage'));
const ModuleDetailPage = React.lazy(() => import('../../pages/learning/ModuleDetailPage'));
const QuizAttemptPage = React.lazy(() => import('../../pages/learning/QuizAttemptPage'));
const QuizResultsPage = React.lazy(() => import('../../pages/learning/QuizResultsPage'));
const LearningProgressPage = React.lazy(() => import('../../pages/learning/LearningProgressPage'));

const AnnouncementsPage = React.lazy(() => import('../../pages/announcements/AnnouncementsPage'));
const AnnouncementDetailPage = React.lazy(() => import('../../pages/announcements/AnnouncementDetailPage'));

// Chat
const ChatPage = React.lazy(() => import('../../pages/chat/ChatPage'));
const ThreadsPage = React.lazy(() => import('../../pages/chat/ThreadsPage'));

// Calendar & Documents
const CalendarPage = React.lazy(() => import('../../pages/calendar/CalendarPage'));
const DocumentsPage = React.lazy(() => import('../../pages/documents/DocumentsPage'));

// Streaming
const YouTubeLivePage = React.lazy(() => import('../../pages/streaming/YouTubeLivePage'));

// Pulse (Internal Social Media)
const PulsePage = React.lazy(() => import('../../pages/pulse/PulsePage'));

// Store (Points Redemption)
const StorePage = React.lazy(() => import('../../pages/store/StorePage'));

// Rewards
const RewardsStorePage = React.lazy(() => import('../../pages/rewards/RewardsMarketplacePage'));
const RewardsDashboardPage = React.lazy(() => import('../../pages/rewards/RewardsDashboardPage'));

// Recognition
const RecognitionDashboardPage = React.lazy(() => import('../../pages/recognition/RecognitionDashboardPage'));
const RecognitionAdminPage = React.lazy(() => import('../../pages/admin/recognition/RecognitionAdminPage'));

// Admin
const AdminDashboard = React.lazy(() => import('../../pages/admin/AdminDashboard'));
const UserManagement = React.lazy(() => import('../../pages/admin/UserManagement'));
const CompanyManagement = React.lazy(() => import('../../pages/admin/CompanyManagement'));
const ContentManagement = React.lazy(() => import('../../pages/admin/ContentManagement'));
const AnnouncementsManagement = React.lazy(() => import('../../pages/admin/ContentManagement').then(m => ({ default: m.AnnouncementsManagement })));
const PoliciesManagement = React.lazy(() => import('../../pages/admin/ContentManagement').then(m => ({ default: m.PoliciesManagement })));
const CourseManagement = React.lazy(() => import('../../pages/admin/CourseManagement'));
const AnalyticsPage = React.lazy(() => import('../../pages/admin/AnalyticsPage'));
const SSOConfigPage = React.lazy(() => import('../../pages/admin/SSOConfigPage'));
const SystemJobsPage = React.lazy(() => import('../../pages/admin/SystemJobsPage'));
const GameManagement = React.lazy(() => import('../../pages/admin/games/GameManagement'));
const RewardsManagement = React.lazy(() => import('../../pages/admin/rewards/RewardsManagement'));
const GameCreator = React.lazy(() => import('../../pages/admin/GameCreator'));
const QuestionnaireManagement = React.lazy(() => import('../../pages/admin/QuestionnaireManagement'));
const DailyWellnessQuestionsManagement = React.lazy(() => import('../../pages/admin/wellness/DailyWellnessQuestionsManagement'));
const CourseEditor = React.lazy(() => import('../../pages/admin/CourseEditor'));
const LessonEditor = React.lazy(() => import('../../pages/admin/LessonEditor'));
const ModuleEditor = React.lazy(() => import('../../pages/admin/ModuleEditor'));
const PolicyEditor = React.lazy(() => import('../../pages/admin/PolicyEditor'));
const AnnouncementEditor = React.lazy(() => import('../../pages/admin/AnnouncementEditor'));
const UserDetailPage = React.lazy(() => import('../../pages/admin/UserDetailPage'));
const UserEditor = React.lazy(() => import('../../pages/admin/UserEditor'));
const QuestionnaireDetailPage = React.lazy(() => import('../../pages/admin/QuestionnaireDetailPage'));
const QuestionnaireEditor = React.lazy(() => import('../../pages/admin/QuestionnaireEditor'));
const GameEditor = React.lazy(() => import('../../pages/admin/GameEditor'));
const GameLevelEditor = React.lazy(() => import('../../pages/admin/GameLevelEditor'));
const LMSIntegrationPage = React.lazy(() => import('../../pages/admin/LMSIntegrationPage'));
const ReportDetailPage = React.lazy(() => import('../../pages/admin/ReportDetailPage'));
const DepartmentDetailPage = React.lazy(() => import('../../pages/admin/DepartmentDetailPage'));
const DepartmentEditor = React.lazy(() => import('../../pages/admin/DepartmentEditor'));
const ContentModerationPage = React.lazy(() => import('../../pages/admin/ContentModerationPage'));
const PolicyAcknowledgmentReport = React.lazy(() => import('../../pages/admin/PolicyAcknowledgmentReport'));
const AuditLogPage = React.lazy(() => import('../../pages/admin/AuditLogPage'));
const SystemSettingsPage = React.lazy(() => import('../../pages/admin/SystemSettingsPage'));
const NotificationManagementPage = React.lazy(() => import('../../pages/admin/NotificationManagementPage'));
const BackupRestorePage = React.lazy(() => import('../../pages/admin/BackupRestorePage'));
const IntegrationSettingsPage = React.lazy(() => import('../../pages/admin/IntegrationSettingsPage'));
const RolePermissionsPage = React.lazy(() => import('../../pages/admin/RolePermissionsPage'));
const ModuleSettingsPage = React.lazy(() => import('../../pages/admin/ModuleSettingsPage'));
const GameEditorAdvanced = React.lazy(() => import('../../pages/admin/GameEditorAdvanced'));
const SuperAdminDashboard = React.lazy(() => import('../../pages/admin/SuperAdminDashboard'));
const SystemConfigPage = React.lazy(() => import('../../pages/admin/SystemConfigPage'));
const UserImportPage = React.lazy(() => import('../../pages/admin/UserImportPage'));
const RewardPointsConfigPage = React.lazy(() => import('../../pages/admin/RewardPointsConfigPage'));
const StoreAdminPage = React.lazy(() => import('../../pages/admin/StoreAdminPage'));
const AdminSettingsPage = React.lazy(() => import('../../pages/admin/AdminSettingsPage'));
const AdminHub = React.lazy(() => import('../../pages/admin/AdminHub'));
const OrgHierarchyPage = React.lazy(() => import('../../pages/admin/OrgHierarchyPage'));
const LMSManagement = React.lazy(() => import('../../pages/admin/LMSManagement'));
const CompanyBrandingPage = React.lazy(() => import('../../pages/admin/CompanyBrandingPage'));

// Tasks
const KanbanBoard = React.lazy(() => import('../../pages/tasks/KanbanBoard'));
const MyTasksPage = React.lazy(() => import('../../pages/tasks/MyTasksPage'));
const TaskManagerPage = React.lazy(() => import('../../pages/tasks/TaskManagerPage'));

const ReportsPage = React.lazy(() => import('../../pages/reports/ReportsPage'));

// Projects & Tasks
const ProjectsPage = React.lazy(() => import('../../pages/projects/ProjectsPage'));
const ProjectDetailsPage = React.lazy(() => import('../../pages/projects/ProjectDetailsPage'));
const TaskBoardPage = React.lazy(() => import('../../pages/projects/TaskBoardPage'));
const TaskDetailPage = React.lazy(() => import('../../pages/projects/TaskDetailPage'));
const ProjectSettingsPage = React.lazy(() => import('../../pages/projects/ProjectSettingsPage'));

const TeamMembersPage = React.lazy(() => import('../../pages/team/TeamMembersPage'));
const TeamMemberDetailPage = React.lazy(() => import('../../pages/team/TeamMemberDetailPage'));
const TeamSentimentPage = React.lazy(() => import('../../pages/team/TeamSentimentPage'));
const TeamAnalyticsPage = React.lazy(() => import('../../pages/team/TeamAnalyticsPage'));

const WellnessPage = React.lazy(() => import('../../pages/wellness/WellnessPage'));
const WellnessHistoryPage = React.lazy(() => import('../../pages/wellness/WellnessHistoryPage'));
const WellnessDetailPage = React.lazy(() => import('../../pages/wellness/WellnessDetailPage'));
const WellnessAnalyticsPage = React.lazy(() => import('../../pages/wellness/WellnessAnalyticsPage'));

const ProfileEditor = React.lazy(() => import('../../pages/profile/ProfileEditor'));
const NotificationSettingsPage = React.lazy(() => import('../../pages/profile/NotificationSettingsPage'));
const SecuritySettingsPage = React.lazy(() => import('../../pages/profile/SecuritySettingsPage'));
const PreferencesPage = React.lazy(() => import('../../pages/profile/PreferencesPage'));

const DiscussionThreadPage = React.lazy(() => import('../../pages/social/DiscussionThreadPage'));
const CreateAnnouncementPage = React.lazy(() => import('../../pages/social/CreateAnnouncementPage'));

// Culture Hub
const CultureHubPage = React.lazy(() => import('../../pages/culture/CultureHubPage'));
const CultureAnalyticsPage = React.lazy(() => import('../../pages/culture/CultureAnalyticsPage'));

// Error pages
const NotFoundPage = React.lazy(() => import('../../pages/error/NotFoundPage'));
const UnauthorizedPage = React.lazy(() => import('../../pages/error/UnauthorizedPage'));

function AppRoutes() {
  const { isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={
          <PublicRoute>
            <EnterpriseLoginPage />
          </PublicRoute>
        } />
        <Route path="/register" element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        } />
        <Route path="/forgot-password" element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        } />
        <Route path="/reset-password" element={
          <PublicRoute>
            <ResetPasswordPage />
          </PublicRoute>
        } />

        {/* Onboarding Routes */}
        <Route path="/questionnaire" element={
          <ProtectedRoute>
            <QuestionnairePage />
          </ProtectedRoute>
        } />

        {/* Public Landing Page */}
        <Route path="/" element={
          <PublicRoute>
            <EnterpriseLandingPage />
          </PublicRoute>
        } />

        {/* Protected Routes */}

        <Route path="/dashboard" element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } />

        <Route path="/profile" element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        } />
        <Route path="/profile/edit" element={
          <ProtectedRoute>
            <ProfileEditor />
          </ProtectedRoute>
        } />
        <Route path="/profile/notifications" element={
          <ProtectedRoute>
            <NotificationSettingsPage />
          </ProtectedRoute>
        } />
        <Route path="/profile/security" element={
          <ProtectedRoute>
            <SecuritySettingsPage />
          </ProtectedRoute>
        } />
        <Route path="/profile/preferences" element={
          <ProtectedRoute>
            <PreferencesPage />
          </ProtectedRoute>
        } />

        <Route path="/settings" element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        } />

        {/* Gamification Routes */}
        <Route path="/gamification" element={
          <ProtectedRoute>
            <GamesHub />
          </ProtectedRoute>
        } />
        <Route path="/gamification/quiz/:id" element={
          <ProtectedRoute>
            <QuizPage />
          </ProtectedRoute>
        } />
        <Route path="/gamification/leaderboard" element={
          <ProtectedRoute>
            <LeaderboardPage />
          </ProtectedRoute>
        } />
        <Route path="/gamification/achievements" element={
          <ProtectedRoute>
            <AchievementsPage />
          </ProtectedRoute>
        } />
        <Route path="/gamification/challenges" element={
          <ProtectedRoute>
            <ChallengesPage />
          </ProtectedRoute>
        } />
        <Route path="/gamification/points" element={
          <ProtectedRoute>
            <PointsHistoryPage />
          </ProtectedRoute>
        } />
        <Route path="/gamification/points-history" element={
          <ProtectedRoute>
            <PointsHistoryPage />
          </ProtectedRoute>
        } />
        <Route path="/gamification/achievements/:id" element={
          <ProtectedRoute>
            <AchievementDetailPage />
          </ProtectedRoute>
        } />
        <Route path="/gamification/games/:id" element={
          <ProtectedRoute>
            <GameDetailPage />
          </ProtectedRoute>
        } />
        <Route path="/gamification/challenges/:id" element={
          <ProtectedRoute>
            <ChallengeDetailPage />
          </ProtectedRoute>
        } />
        <Route path="/gamification/leaderboard/:type" element={
          <ProtectedRoute>
            <LeaderboardDetailPage />
          </ProtectedRoute>
        } />

        {/* Games Routes */}
        <Route path="/games" element={
          <ProtectedRoute>
            <GamesHub />
          </ProtectedRoute>
        } />
        <Route path="/games/memory" element={
          <ProtectedRoute>
            <MemoryGame />
          </ProtectedRoute>
        } />
        <Route path="/games/word-puzzle" element={
          <ProtectedRoute>
            <WordPuzzleGame />
          </ProtectedRoute>
        } />
        <Route path="/games/trivia" element={
          <ProtectedRoute>
            <TriviaGame />
          </ProtectedRoute>
        } />
        <Route path="/games/reaction" element={
          <ProtectedRoute>
            <ReactionGame />
          </ProtectedRoute>
        } />
        <Route path="/games/speed-math" element={
          <ProtectedRoute>
            <SpeedMathGame />
          </ProtectedRoute>
        } />
        <Route path="/games/pattern" element={
          <ProtectedRoute>
            <PatternGame />
          </ProtectedRoute>
        } />
        <Route path="/games/narrative-quest/:id" element={
          <ProtectedRoute>
            <NarrativeQuestEngine />
          </ProtectedRoute>
        } />
        <Route path="/games/scavenger-expedition/:id" element={
          <ProtectedRoute>
            <ScavengerExpeditionEngine />
          </ProtectedRoute>
        } />
        <Route path="/games/world-building/:id" element={
          <ProtectedRoute>
            <WorldBuildingEngine />
          </ProtectedRoute>
        } />
        <Route path="/games/role-playing/:id" element={
          <ProtectedRoute>
            <RolePlayingEngine />
          </ProtectedRoute>
        } />
        <Route path="/games/micro-challenge/:id" element={
          <ProtectedRoute>
            <MicroChallengeEngine />
          </ProtectedRoute>
        } />
        <Route path="/games/fitness-coach" element={
          <ProtectedRoute>
            <FitnessCoachGame />
          </ProtectedRoute>
        } />
        <Route path="/games/wellness-quiz" element={
          <ProtectedRoute>
            <WellnessQuizGame />
          </ProtectedRoute>
        } />
        <Route path="/games/meal-planner" element={
          <ProtectedRoute>
            <MealPlannerGame />
          </ProtectedRoute>
        } />

        {/* Learning Routes */}
        <Route path="/learning" element={
          <ProtectedRoute>
            <LearningHub />
          </ProtectedRoute>
        } />
        <Route path="/learning/my-learning" element={
          <ProtectedRoute>
            <MyLearningPage />
          </ProtectedRoute>
        } />
        <Route path="/learning/course/:id" element={
          <ProtectedRoute>
            <CoursePage />
          </ProtectedRoute>
        } />
        <Route path="/learning/course-detail/:id" element={
          <ProtectedRoute>
            <CourseDetailPage />
          </ProtectedRoute>
        } />
        <Route path="/learning/module/:id" element={
          <ProtectedRoute>
            <ModuleDetailPage />
          </ProtectedRoute>
        } />
        <Route path="/learning/quiz/:id" element={
          <ProtectedRoute>
            <QuizAttemptPage />
          </ProtectedRoute>
        } />
        <Route path="/learning/quiz/:id/results" element={
          <ProtectedRoute>
            <QuizResultsPage />
          </ProtectedRoute>
        } />
        <Route path="/learning/progress" element={
          <ProtectedRoute>
            <LearningProgressPage />
          </ProtectedRoute>
        } />
        <Route path="/learning/course/:courseId/lesson/:lessonId" element={
          <ProtectedRoute>
            <LessonPlayer />
          </ProtectedRoute>
        } />
        <Route path="/learning/certificate/:courseId" element={
          <ProtectedRoute>
            <CertificatePage />
          </ProtectedRoute>
        } />
        <Route path="/learning/articles" element={
          <ProtectedRoute>
            <ArticlePage />
          </ProtectedRoute>
        } />
        <Route path="/learning/article/:id" element={
          <ProtectedRoute>
            <ArticlePage />
          </ProtectedRoute>
        } />
        <Route path="/learning/videos" element={
          <ProtectedRoute>
            <VideoPage />
          </ProtectedRoute>
        } />
        <Route path="/learning/video/:id" element={
          <ProtectedRoute>
            <VideoPage />
          </ProtectedRoute>
        } />
        <Route path="/learning/podcasts" element={
          <ProtectedRoute>
            <PodcastPage />
          </ProtectedRoute>
        } />
        <Route path="/learning/podcast/:id" element={
          <ProtectedRoute>
            <PodcastPage />
          </ProtectedRoute>
        } />
        <Route path="/learning/microlearning" element={
          <ProtectedRoute>
            <MicrolearningPage />
          </ProtectedRoute>
        } />
        <Route path="/learning/path/:id" element={
          <ProtectedRoute>
            <LearningProgressPage />
          </ProtectedRoute>
        } />
        <Route path="/learning/journey/:id" element={
          <ProtectedRoute>
            <LearningProgressPage />
          </ProtectedRoute>
        } />
        <Route path="/learning/microlearning/:id" element={
          <ProtectedRoute>
            <MicrolearningPage />
          </ProtectedRoute>
        } />
        <Route path="/microlearning" element={
          <ProtectedRoute>
            <MicrolearningPage />
          </ProtectedRoute>
        } />
        <Route path="/microlearning/:id" element={
          <ProtectedRoute>
            <MicrolearningPage />
          </ProtectedRoute>
        } />
        <Route path="/policies" element={
          <ProtectedRoute>
            <CompanyPoliciesPage />
          </ProtectedRoute>
        } />
        <Route path="/policies/:id" element={
          <ProtectedRoute>
            <PolicyDetailPage />
          </ProtectedRoute>
        } />
        <Route path="/announcements" element={
          <ProtectedRoute>
            <AnnouncementsPage />
          </ProtectedRoute>
        } />
        <Route path="/announcements/create" element={
          <ProtectedRoute>
            <CreateAnnouncementPage />
          </ProtectedRoute>
        } />
        <Route path="/announcements/:id" element={
          <ProtectedRoute>
            <AnnouncementDetailPage />
          </ProtectedRoute>
        } />

        {/* Chat Routes */}
        <Route path="/chat" element={
          <ProtectedRoute>
            <ChatPage />
          </ProtectedRoute>
        } />
        <Route path="/chat/:conversationId" element={
          <ProtectedRoute>
            <ChatPage />
          </ProtectedRoute>
        } />
                <Route path="/threads" element={
                  <ProtectedRoute>
                    <ThreadsPage />
                  </ProtectedRoute>
                } />

                {/* Calendar Routes */}
                <Route path="/calendar" element={
                  <ProtectedRoute>
                    <CalendarPage />
                  </ProtectedRoute>
                } />

                                {/* Documents Routes */}
                                <Route path="/documents" element={
                                  <ProtectedRoute>
                                    <DocumentsPage />
                                  </ProtectedRoute>
                                } />

                                {/* Streaming Routes */}
                                <Route path="/streaming" element={
                                  <ProtectedRoute>
                                    <YouTubeLivePage />
                                  </ProtectedRoute>
                                } />
                                <Route path="/live" element={
                                  <ProtectedRoute>
                                    <YouTubeLivePage />
                                  </ProtectedRoute>
                                } />

        {/* Culture Hub Routes */}
        <Route path="/culture" element={
          <ProtectedRoute>
            <CultureHubPage />
          </ProtectedRoute>
        } />
        <Route path="/culture/analytics" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <CultureAnalyticsPage />
          </ProtectedRoute>
        } />

        <Route path="/marketplace" element={<Navigate to="/store" replace />} />
        <Route path="/marketplace/*" element={<Navigate to="/store" replace />} />

                {/* Store (Points Redemption) */}
                <Route path="/store" element={
                  <ProtectedRoute>
                    <StorePage />
                  </ProtectedRoute>
                } />

                {/* Rewards Routes */}
                <Route path="/rewards" element={
                  <ProtectedRoute>
                    <RewardsDashboardPage />
                  </ProtectedRoute>
                } />
                <Route path="/rewards/dashboard" element={
                  <ProtectedRoute>
                    <RewardsDashboardPage />
                  </ProtectedRoute>
                } />
                <Route path="/rewards/store" element={
                  <ProtectedRoute>
                    <RewardsStorePage />
                  </ProtectedRoute>
                } />
                <Route path="/rewards/marketplace" element={<Navigate to="/rewards/store" replace />} />

                {/* Projects & Tasks Routes */}
                <Route path="/projects" element={
          <ProtectedRoute>
            <ProjectsPage />
          </ProtectedRoute>
        } />
        <Route path="/projects/:id" element={
          <ProtectedRoute>
            <ProjectDetailsPage />
          </ProtectedRoute>
        } />
        <Route path="/projects/:id/board" element={
          <ProtectedRoute>
            <TaskBoardPage />
          </ProtectedRoute>
        } />
        <Route path="/projects/:id/settings" element={
          <ProtectedRoute>
            <ProjectSettingsPage />
          </ProtectedRoute>
        } />
        <Route path="/projects/task/:id" element={
          <ProtectedRoute>
            <TaskDetailPage />
          </ProtectedRoute>
        } />

        {/* Team Routes */}
        <Route path="/team/members" element={
          <ProtectedRoute>
            <TeamMembersPage />
          </ProtectedRoute>
        } />
        <Route path="/team/members/:id" element={
          <ProtectedRoute>
            <TeamMemberDetailPage />
          </ProtectedRoute>
        } />
        <Route path="/team/sentiment" element={
          <ProtectedRoute>
            <TeamSentimentPage />
          </ProtectedRoute>
        } />
        <Route path="/team/analytics" element={
          <ProtectedRoute>
            <TeamAnalyticsPage />
          </ProtectedRoute>
        } />
        
                <Route path="/wellness" element={
                  <ProtectedRoute>
                    <WellnessPage />
                  </ProtectedRoute>
                } />
                <Route path="/wellness/history" element={
                  <ProtectedRoute>
                    <WellnessHistoryPage />
                  </ProtectedRoute>
                } />
        <Route path="/wellness/check-in/:id" element={
          <ProtectedRoute>
            <WellnessDetailPage />
          </ProtectedRoute>
        } />
        <Route path="/wellness/analytics" element={
          <ProtectedRoute requiredRoles={['admin', 'manager', 'company-super-admin', 'super-super-admin']}>
            <WellnessAnalyticsPage />
          </ProtectedRoute>
        } />
        
                <Route path="/social/thread/:id" element={
                  <ProtectedRoute>
                    <DiscussionThreadPage />
                  </ProtectedRoute>
                } />

                {/* Pulse (Internal Social Media) */}
                <Route path="/pulse" element={
                  <ProtectedRoute>
                    <PulsePage />
                  </ProtectedRoute>
                } />

                {/* Alias Routes for convenience */}
        <Route path="/leaderboard" element={
          <ProtectedRoute>
            <LeaderboardPage />
          </ProtectedRoute>
        } />
        <Route path="/analytics" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <AnalyticsPage />
          </ProtectedRoute>
        } />
        <Route path="/reports" element={
          <ProtectedRoute>
            <ReportsPage />
          </ProtectedRoute>
        } />

        {/* Recognition Routes */}
        <Route path="/recognition" element={
          <ProtectedRoute>
            <RecognitionDashboardPage />
          </ProtectedRoute>
        } />

        {/* Admin Routes */}
        <Route path="/admin" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <AdminHub />
          </ProtectedRoute>
        } />
        <Route path="/admin/users" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <UserManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin/companies" element={
          <ProtectedRoute requiredRoles={['company-super-admin', 'super-super-admin']}>
            <CompanyManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin/content" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <ContentManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin/announcements" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <AnnouncementsManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin/policies" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <PoliciesManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin/courses" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <CourseManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin/lms" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <LMSManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin/analytics"element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <AnalyticsPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/modules" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <ModuleSettingsPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/sso" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <SSOConfigPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/system-jobs" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <SystemJobsPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/games" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <GameManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin/rewards" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <RewardsManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin/recognition" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <RecognitionAdminPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/game-creator" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <GameCreator />
          </ProtectedRoute>
        } />
        <Route path="/admin/questionnaires" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <QuestionnaireManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin/daily-wellness-questions" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <DailyWellnessQuestionsManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin/courses/create" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <CourseEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/courses/:id/edit" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <CourseEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/modules/create" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <ModuleEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/modules/:id/edit" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <ModuleEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/lessons/create" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <LessonEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/lessons/:id/edit" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <LessonEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/policies/create" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <PolicyEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/policies/:id/edit" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <PolicyEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/policies/:id/acknowledgments" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <PolicyAcknowledgmentReport />
          </ProtectedRoute>
        } />
        <Route path="/admin/announcements/create" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <AnnouncementEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/announcements/:id/edit" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <AnnouncementEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/users/:id" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <UserDetailPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/users/create" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <UserEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/users/:id/edit" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <UserEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/questionnaires/:id" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <QuestionnaireDetailPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/questionnaires/create" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <QuestionnaireEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/questionnaires/:id/edit" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <QuestionnaireEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/games/create" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <GameEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/games/:id/edit" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <GameEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/games/:gameId/levels" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <GameLevelEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/lms-integration" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <LMSIntegrationPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/reports/:id" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <ReportDetailPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/departments/:id" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <DepartmentDetailPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/departments/create" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <DepartmentEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/departments/:id/edit" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <DepartmentEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/moderation" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <ContentModerationPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/audit-log" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <AuditLogPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/system-settings" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <SystemSettingsPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/settings" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <AdminSettingsPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/notifications" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <NotificationManagementPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/backup" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <BackupRestorePage />
          </ProtectedRoute>
        } />
        <Route path="/admin/integrations" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <IntegrationSettingsPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/roles" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <RolePermissionsPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/games/advanced" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <GameEditorAdvanced />
          </ProtectedRoute>
        } />
        <Route path="/admin/games/advanced/:id" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <GameEditorAdvanced />
          </ProtectedRoute>
        } />
        <Route path="/admin/system-config" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <SystemConfigPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/user-import" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <UserImportPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/points" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <RewardPointsConfigPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/store" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <StoreAdminPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/marketplace" element={<Navigate to="/admin/store" replace />} />
        <Route path="/admin/super" element={
          <ProtectedRoute requiredRoles={['company-super-admin', 'super-super-admin']}>
            <SuperAdminDashboard />
          </ProtectedRoute>
        } />
        <Route path="/admin/org-hierarchy" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <OrgHierarchyPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/org-structure" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <OrgHierarchyPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/wellness-analytics" element={
          <ProtectedRoute requiredRoles={['admin', 'manager', 'company-super-admin', 'super-super-admin']}>
            <WellnessAnalyticsPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/reports" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <ReportsPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/branding" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <CompanyBrandingPage />
          </ProtectedRoute>
        } />

        {/* Super Admin Routes */}
        <Route path="/super-admin" element={
          <ProtectedRoute requiredRoles={['super-super-admin']}>
            <SuperAdminDashboard />
          </ProtectedRoute>
        } />
        <Route path="/super-admin/companies" element={
          <ProtectedRoute requiredRoles={['super-super-admin']}>
            <SuperAdminDashboard />
          </ProtectedRoute>
        } />

        {/* Onboarding Routes */}
        <Route path="/onboarding" element={
          <ProtectedRoute>
            <OnboardingFlow />
          </ProtectedRoute>
        } />

        {/* Task Management Routes */}
        <Route path="/tasks" element={
          <ProtectedRoute>
            <MyTasksPage />
          </ProtectedRoute>
        } />
        <Route path="/tasks/my-tasks" element={
          <ProtectedRoute>
            <MyTasksPage />
          </ProtectedRoute>
        } />
        <Route path="/tasks/kanban" element={
          <ProtectedRoute>
            <KanbanBoard />
          </ProtectedRoute>
        } />
        <Route path="/tasks/manager" element={
          <ProtectedRoute>
            <TaskManagerPage />
          </ProtectedRoute>
        } />

        {/* Error Routes */}
        <Route path="/unauthorized" element={<UnauthorizedPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

export default AppRoutes;
